import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getReportPdf, type PdfDisposition } from './api';

/** What went wrong, in the words `reports.pdf.errors.*` gives each one (U9). */
export type PdfError =
  | 'REPORT_FORBIDDEN'
  | 'REPORT_NOT_FOUND'
  | 'OWNER_DETAILS_UNAVAILABLE'
  | 'ORGANIZATION_DETAILS_UNAVAILABLE'
  | 'NOTE_PDF_BUSY'
  | 'tabBlocked'
  | 'other';

const KNOWN: readonly string[] = [
  'REPORT_FORBIDDEN',
  'REPORT_NOT_FOUND',
  'OWNER_DETAILS_UNAVAILABLE',
  'ORGANIZATION_DETAILS_UNAVAILABLE',
  // R-e: the server is already making too many PDFs; the same request works a little later.
  'NOTE_PDF_BUSY',
];

/** A minute is ample for the new tab to read the file; then the blob is let go. */
const INLINE_URL_LIFETIME_MS = 60_000;

/** The server's file name, `nota-spese-{number}.pdf` with the number's `/` turned into `-`. */
export function pdfFileName(number: string): string {
  return `nota-spese-${number.replaceAll('/', '-')}.pdf`;
}

/** The note a PDF is asked for: its id for the route, its number for the file's name. */
export type PdfNote = { readonly id: string; readonly number: string };

/** Which note's PDF is on its way, and how; or what went wrong with which one. */
export type PdfBusy = { readonly id: string; readonly disposition: PdfDisposition };
export type PdfFailure = PdfNote & {
  readonly code: PdfError;
  /** What was asked, so «Riprova tra poco» asks it again. */
  readonly disposition: PdfDisposition;
};

/**
 * «Apri» and «Scarica» on a note's PDF, for the note's page and for the list's rows. The file needs
 * the bearer token, so it is fetched as a blob: «Apri» opens its tab at the click (a tab opened
 * after the wait is blocked) and points it at the blob when it arrives; «Scarica» saves it under
 * the server's name.
 */
export function useNotePdf() {
  const { t } = useTranslation('expenses');
  const [busy, setBusy] = useState<PdfBusy | null>(null);
  const [error, setError] = useState<PdfFailure | null>(null);

  const run = async (note: PdfNote, disposition: PdfDisposition) => {
    const tab = disposition === 'inline' ? window.open('', '_blank') : null;
    if (disposition === 'inline' && tab === null) {
      setError({ id: note.id, number: note.number, code: 'tabBlocked', disposition });
      return;
    }
    setBusy({ id: note.id, disposition });
    setError(null);
    const outcome = await getReportPdf(note.id, disposition);
    setBusy(null);
    if (!outcome.ok) {
      tab?.close();
      const code = KNOWN.includes(outcome.code) ? (outcome.code as PdfError) : 'other';
      setError({ id: note.id, number: note.number, code, disposition });
      return;
    }
    const url = URL.createObjectURL(outcome.value);
    if (tab !== null) {
      tab.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), INLINE_URL_LIFETIME_MS);
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = pdfFileName(note.number);
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(t('reports.pdf.saved', { name: pdfFileName(note.number) }));
  };

  /** «Riprova tra poco» on a busy server: the same note, the same way, from a new click. */
  const retry = () => {
    if (error !== null) void run(error, error.disposition);
  };

  return { busy, error, run, retry };
}

export type NotePdf = ReturnType<typeof useNotePdf>;
